import { applyTransaction, arrayAdd, arrayRemove } from '@datorama/akita';
import { parseISO } from 'date-fns';
import { type Subscription, tap } from 'rxjs';
import { query } from './query';
import store, { type MinimumSafeguardsState } from './store';
import { responsesQuery } from 'state/BusinessActivities/query';
import { responsesStore } from 'state/BusinessActivities/store';
import CRUDService from 'state/CRUDService';
import { type ISO8601DateOnlyString, type Organisation, type TaxonomyCriterionResponse } from 'types/models';
import { TaxonomyContributionType } from 'utils/enum';

export default class MinimumSafeguardsService extends CRUDService<MinimumSafeguardsState> {
   private static instance: MinimumSafeguardsService;
   private responsesStore = responsesStore;
   private responsesQuery = responsesQuery;
   private responsesSubscription?: Subscription;
   private statusSubscription?: Subscription;
   constructor() {
      if (!MinimumSafeguardsService.instance) {
         super('taxonomy/minimumsafeguards', store, query, {
            useOrganisationContext: true,
            usePeriodContext: true,
         });
         MinimumSafeguardsService.instance = this;
      }
      // Service shall be instantiated only once, because otherwise the observable will be created for each service instance
      return MinimumSafeguardsService.instance;
   }

   async getEntities({ force = false } = {}) {
      void super.getEntities({ force });

      if (force && this.responsesSubscription && !this.responsesSubscription.closed) {
         this.responsesSubscription.unsubscribe();
         this.responsesSubscription = undefined;
      }

      if (this.responsesSubscription == undefined || this.responsesSubscription?.closed) {
         this.responsesSubscription = this.queryParamsObservable
            .pipe(
               tap((queryString) => {
                  if (typeof queryString === 'string') {
                     this.httpClient
                        .get(`/${this.version}/${this.entityName}/responses${queryString}`)
                        .then((resp) => this.responsesStore.upsertMany(resp.data))
                        .catch((error) => {
                           this.setError(error, responsesStore);
                        });
                  }
               })
            )
            .subscribe();
      }
   }

   getStatus() {
      if (this.statusSubscription == undefined || this.statusSubscription?.closed) {
         this.statusSubscription = this.queryParamsObservable
            .pipe(
               tap((queryString) => {
                  if (typeof queryString === 'string') {
                     this.httpClient
                        .get(`/${this.version}/${this.entityName}/status${queryString}`)
                        .then((resp) => this.store.update({ status: resp.data }))
                        .catch((error) => {
                           this.setError(error, responsesStore);
                        });
                  }
               })
            )
            .subscribe();
      }
   }

   async createCriterionResponse(criterionResponse: any) {
      return this.httpClient
         .post(`/${this.version}/taxonomy/criteriaresponses`, criterionResponse)
         .then((resp) => {
            this.responsesStore.add(resp.data);
            this.getStatus();
            return resp.data.id;
         })
         .catch((error) => {
            this.setError(error, responsesStore);
         });
   }

   async deleteCriterionResponse(criterionResponseIdOrArray: TaxonomyCriterionResponse['id'] | TaxonomyCriterionResponse['id'][]) {
      let promise;

      if (Array.isArray(criterionResponseIdOrArray) && criterionResponseIdOrArray.length > 0) {
         promise = this.httpClient.delete(`/${this.version}/taxonomy/criteriaresponses`, { data: criterionResponseIdOrArray });
      } else if (criterionResponseIdOrArray) {
         promise = this.httpClient.delete(`/${this.version}/taxonomy/criteriaresponses/${criterionResponseIdOrArray.toString()}`);
      }

      if (promise) {
         return promise
            .then(() => {
               this.getStatus();
               return this.responsesStore.remove(criterionResponseIdOrArray);
            })
            .catch((error) => {
               this.setError(error, responsesStore);
            });
      }

      return undefined;
   }

   async clearResponses(organisationId: Organisation['id'], from: Date | ISO8601DateOnlyString, to: Date | ISO8601DateOnlyString) {
      const responses = this.responsesQuery
         .getAll()
         .filter(
            (entity) =>
               entity?.criterion?.contributionType?.code === TaxonomyContributionType.MINIMUM_SAFEGUARDS &&
               entity?.organisation?.id === organisationId &&
               parseISO(entity.validFrom) >= (from instanceof Date ? from : parseISO(from)) &&
               parseISO(entity.validTo) <= (to instanceof Date ? to : parseISO(to))
         );

      return this.deleteCriterionResponse(responses.map((resp) => resp.id));
   }

   async persistCriteriaResponseChanges(changes: Partial<TaxonomyCriterionResponse>[] = [], refresh = true) {
      this.responsesStore.setLoading(true);

      const finalChanges = Array.from(changes).filter(({ id }) => !!id);

      void applyTransaction(() =>
         Promise.all(
            finalChanges
               .filter((c) => c.id)
               .map(({ id: responseId, ...change }) =>
                  this.httpClient.patch(`/${this.version}/taxonomy/criteriaresponses/${responseId}`, change).then((resp) => resp.data)
               )
         )
            .then((responses) => {
               this.responsesStore.upsertMany(responses);
               if (refresh) {
                  this.getStatus();
               }
               return this.responsesStore.setLoading(false);
            })
            .catch((err) => {
               this.responsesStore.setLoading(false);
               return this.setError(err, this.responsesStore);
            })
      );
   }

   hasExistingResponses(organisationId: Organisation['id'], from: Date | ISO8601DateOnlyString, to: Date | ISO8601DateOnlyString) {
      return this.responsesQuery.hasEntity(
         (entity: TaxonomyCriterionResponse) =>
            entity?.organisation?.id === organisationId &&
            entity?.criterion?.contributionType?.code === TaxonomyContributionType.MINIMUM_SAFEGUARDS &&
            parseISO(entity.validFrom) >= (from instanceof Date ? from : parseISO(from)) &&
            parseISO(entity.validTo) <= (to instanceof Date ? to : parseISO(to))
      );
   }

   async uploadResponseAttachments(responseId: TaxonomyCriterionResponse['id'], files: File[]) {
      return Promise.all(
         files.map((file) => {
            const formData = new FormData();
            formData.append('attachment', file);

            return this.httpClient
               .post(`/${this.version}/taxonomy/criteriaresponses/${responseId}/attachments`, formData, {
                  headers: { 'Content-Type': 'multipart/form-data' },
               })
               .then((resp) => {
                  this.responsesStore.update(responseId, ({ attachments = [] }) => ({ attachments: arrayAdd(attachments, resp.data) }));

                  return resp.data;
               })
               .catch((error) => {
                  this.setError(error);
               });
         })
      );
   }

   async linkAttachmentToResponse(responseId: TaxonomyCriterionResponse['id'], attachmentId: number) {
      const attachment = await this.linkAttachment(attachmentId, { modelId: responseId, modelName: 'taxonomyCriteriaResponse' });

      if (attachment) {
         this.responsesStore.update(responseId, ({ attachments = [] }) => ({ attachments: arrayAdd(attachments, attachment) }));
         return attachment;
      }
   }

   async unlinkAttachmentFromResponse(responseId: TaxonomyCriterionResponse['id'], attachmentId: number) {
      const unlinked = await this.unlinkAttachment(attachmentId, { modelId: responseId, modelName: 'taxonomyCriteriaResponse' });
      if (unlinked) {
         this.responsesStore.update(responseId, ({ attachments = [] }) => ({ attachments: arrayRemove(attachments, attachmentId) }));
      }

      return unlinked;
   }

   async deleteResponseAttachment(responseId: number, responseAttachmentId: number) {
      this.httpClient
         .delete(`/${this.version}/taxonomy/criteriaresponses/${responseId}/attachments/${responseAttachmentId}`)
         .then(() =>
            this.responsesStore.update(responseId, ({ attachments = [] }) => ({ attachments: arrayRemove(attachments, responseAttachmentId) }))
         )
         .catch((error) => {
            this.setError(error);
         });
   }

   setStatus(
      organisationId: Organisation['id'],
      from: Date | ISO8601DateOnlyString,
      to: Date | ISO8601DateOnlyString,
      setStatusValues: any,
      auditValues: any
   ) {
      this.responsesStore.update(
         (entity: TaxonomyCriterionResponse) =>
            auditValues.includes(entity.id) &&
            entity.criterion.contributionType.code === TaxonomyContributionType.MINIMUM_SAFEGUARDS &&
            entity.organisation.id === organisationId &&
            parseISO(entity.validFrom) >= (from instanceof Date ? from : parseISO(from)) &&
            parseISO(entity.validTo) <= (to instanceof Date ? to : parseISO(to)),
         { ...setStatusValues }
      );
   }

   unsubscribe() {
      super.unsubscribe();

      if (this.statusSubscription && !this.statusSubscription?.closed) {
         this.statusSubscription.unsubscribe();
      }

      if (this.responsesSubscription && !this.responsesSubscription?.closed) {
         this.responsesSubscription.unsubscribe();
      }
   }
}
